import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAccount } from '../context/AccountContext';

const GOOGLE_SCRIPT_ID = 'google-identity-services';

// El script de Google a veces falla la primera vez (503 puntual, red lenta).
// Sin reintento, esa unica peticion fallida deja el boton de login vacio
// para toda la sesion aunque Google se recupere segundos despues.
function loadGoogleScript(attempt = 1) {
    if (window.google?.accounts?.id) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const existing = document.getElementById(GOOGLE_SCRIPT_ID);
        if (existing) {
            existing.remove();
        }

        const script = document.createElement('script');
        script.id = GOOGLE_SCRIPT_ID;
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('No se pudo cargar Google Identity Services'));
        document.head.appendChild(script);
    }).catch((error) => {
        if (attempt >= 3) {
            throw error;
        }
        return new Promise((resolve) => setTimeout(resolve, 1000 * attempt))
            .then(() => loadGoogleScript(attempt + 1));
    });
}

const GoogleSignInButton = () => {
    const buttonRef = useRef(null);
    const { refresh } = useAccount();
    // Sesion 2026-08-13: el usuario reporto que el login con Google "se
    // quedaba cargando" -- el flujo real funciona (popup/FedCM de Google,
    // fuera de nuestro control), pero entre el clic y que fetch('/api/auth/
    // google') resuelva no habia ninguna señal visual, solo el boton de
    // Google tal cual. Este estado es solo para mostrar esa espera.
    const [pending, setPending] = useState(false);

    useEffect(() => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!clientId) {
            return;
        }

        let cancelled = false;

        loadGoogleScript().then(() => {
            if (cancelled || !window.google?.accounts?.id || !buttonRef.current) {
                return;
            }

            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: async (response) => {
                    setPending(true);
                    try {
                        const authResponse = await fetch('/api/auth/google', {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ idToken: response.credential })
                        });

                        if (!authResponse.ok) {
                            const data = await authResponse.json().catch(() => ({}));
                            console.error('[google-signin] Fallo el login:', authResponse.status, data.error);
                            toast.error('No se pudo iniciar sesion con Google. Intentalo de nuevo.');
                        }
                    } catch (error) {
                        console.error('[google-signin] Error de red iniciando sesion:', error.message);
                        toast.error('No se pudo iniciar sesion con Google. Revisa tu conexion.');
                    } finally {
                        setPending(false);
                        refresh();
                    }
                }
            });

            // Sin un "width" explicito, Google decide su propio ancho por
            // defecto, que en un contenedor movil estrecho (el modal de login)
            // no encaja bien y el boton se renderiza roto/recortado. Medimos
            // el ancho real disponible y se lo pasamos, dentro del rango que
            // acepta la API (200-400px).
            const availableWidth = buttonRef.current.offsetWidth || 300;
            window.google.accounts.id.renderButton(buttonRef.current, {
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                locale: 'es',
                width: Math.min(Math.max(availableWidth, 200), 400)
            });
        }).catch((error) => {
            console.error('[google-signin] No se pudo cargar el boton de Google:', error.message);
        });

        return () => {
            cancelled = true;
        };
    }, [refresh]);

    return (
        <div>
            <div ref={buttonRef} style={pending ? { pointerEvents: 'none', opacity: 0.6 } : undefined} />
            {pending && (
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#5d6b7a', textAlign: 'center' }}>
                    Conectando con Google...
                </p>
            )}
        </div>
    );
};

export default GoogleSignInButton;
